import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useT } from '../i18n';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';

export default function RedefinirSenha() {
  const { t } = useT();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get('token') || '');
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!token) {
      toast.error(t('redefinir.erro_token'));
      return;
    }
    if (senha.length < 6) {
      toast.error(t('redefinir.erro_curta'));
      return;
    }
    if (senha !== confirmar) {
      toast.error(t('redefinir.erro_diferente'));
      return;
    }

    setCarregando(true);
    try {
      await api.post('/auth/redefinir-senha', { token, senha });
      setSucesso(true);
      toast.success(t('redefinir.sucesso'));
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.erro || t('redefinir.erro_generico'));
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `
        radial-gradient(900px 500px at 100% 0%, rgba(22, 163, 74,0.18), transparent 60%),
        radial-gradient(700px 500px at 0% 100%, rgba(139,92,246,0.18), transparent 60%),
        linear-gradient(180deg, #081832 0%, #0b1f3f 100%)
      `,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem'
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.98)',
        padding: '2.5rem',
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(15,17,32,0.45)',
        width: '100%', maxWidth: 440
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11,
            background: 'linear-gradient(135deg, #16a34a, #13305f)',
            display: 'grid', placeItems: 'center', color: '#fff',
            fontWeight: 700, boxShadow: '0 8px 20px rgba(22, 163, 74,0.4)'
          }}>D</div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{t('redefinir.titulo')}</h2>
            <p style={{ color: '#8a8f9f', fontSize: 12, margin: '2px 0 0' }}>{t('redefinir.subtitulo')}</p>
          </div>
        </div>

        {sucesso ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <CheckCircle2 size={56} color="#22c55e" style={{ margin: '0 auto 12px', display: 'block' }} />
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{t('redefinir.sucesso_titulo')}</h3>
            <p style={{ color: '#666', fontSize: 14, margin: 0 }}>{t('redefinir.sucesso_redirect')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={inputLabel}>{t('redefinir.label_nova_senha')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={mostrar ? 'text' : 'password'}
                  value={senha} onChange={e => setSenha(e.target.value)}
                  placeholder="••••••••" required minLength={6}
                  style={{ ...inputField, paddingRight: 42 }}
                />
                <button
                  type="button"
                  onClick={() => setMostrar(v => !v)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 6, display: 'grid', placeItems: 'center', color: '#8a8f9f'
                  }}
                >
                  {mostrar ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={inputLabel}>{t('redefinir.label_confirmar')}</label>
              <input
                type={mostrar ? 'text' : 'password'}
                value={confirmar} onChange={e => setConfirmar(e.target.value)}
                placeholder="••••••••" required minLength={6}
                style={inputField}
              />
            </div>
            <button type="submit" disabled={carregando || !token} style={{
              width: '100%', padding: 12, color: '#fff',
              background: carregando ? '#9aa0b6' : 'linear-gradient(135deg, #16a34a, #13305f)',
              border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: carregando ? 'not-allowed' : 'pointer',
              boxShadow: '0 10px 24px rgba(22, 163, 74,0.25)'
            }}>
              {carregando ? t('redefinir.btn_salvando') : t('redefinir.btn_salvar')}
            </button>

            {!token && (
              <p style={{ marginTop: 12, fontSize: 12, color: '#dc2626', textAlign: 'center' }}>
                {t('redefinir.erro_sem_token')}
              </p>
            )}

            <p style={{ marginTop: 18, fontSize: 12, color: '#8a8f9f', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{ background: 'none', border: 'none', color: '#12833b', fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >
                ← {t('redefinir.voltar_login')}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const inputLabel = {
  fontSize: 12.5, color: '#5b6173', display: 'block',
  marginBottom: 6, fontWeight: 500
};
const inputField = {
  width: '100%', padding: '11px 13px', borderRadius: 10,
  border: '1px solid #e6e8ef', fontSize: 16,
  boxSizing: 'border-box', background: '#fafbfd'
};
